/**
 * Location Hub — the redesigned `/sites/:siteId` page.
 *
 * Reads as a file folder: header card with identity, per-trade
 * summary chips, then a tab strip whose contents auto-discover from
 * which trades have devices/jobs at this site. A `+ Add service line`
 * popover reveals any trades that haven't been tagged yet but are
 * available org-wide (sourced from `/summary`'s `hiddenTrades`).
 *
 * Add/Edit device modals are preserved from the previous page so the
 * office team doesn't have to re-learn anything. Asset rows now route
 * to the new `/assets/:id` page; the inline edit still works for
 * one-off field tweaks.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { BackLink } from "@/components/back-link";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Badge } from "@/components/ui/badge";
import { Button, buttonVariants } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import {
    BackflowDeviceFields,
    isDetectorAssembly,
    type BackflowDeviceFieldValues,
} from "@/components/backflow-device-fields";
import { toast } from "sonner";
import {
    ArrowLeft,
    MapPin,
    Plus,
    X,
    AlertCircle,
    Sparkles,
    Phone,
    Mail,
    User,
    Pencil,
    KeyRound,
    Search,
    ShieldCheck,
    FileText,
} from "lucide-react";
import type { TradeSummary, SiteContext } from "./location/types";
import { DevicesSection } from "./location/devices-section";
import { DeviceMapView } from "./location/device-map-view";
import type { MappableDevice } from "./location/device-map";
import { TradeContextStrip } from "./location/trade-context-strip";
import { TestsSection } from "./location/tests-section";
import { JobsSection } from "./location/jobs-section";
import { DocumentsSection } from "./location/documents-section";
import { PhotosSection } from "./location/photos-section";
import { EstimatesSection, InvoicesSection } from "./location/money-sections";
import { TaskListInline } from "@/components/task-list-inline";
import { FromTheFieldSection } from "@/components/field-add/field-add-office";
import { ComplianceCard, LocationHeadline } from "./location/compliance-panel";
import {
    LOCATION_TABS,
    locationViewToSearch,
    parseLocationView,
    testsUnavailableReason,
    type LocationTabId,
} from "./location/tabs";
import { SiteWaterReportingCard } from "./site-water-card";
import { ReconcileFromListDialog } from "./reconcile-from-list";
import { newEstimateHref, newJobHref, paymentProfileChip, reconcileEntryVisible } from "./location-page-v2";
import { isContactStale, formatVerifiedLabel } from "@/lib/contactFreshness";
import { AddDeviceToSiteModal, SERVICE_TYPES, deviceErrorMessage } from "@/components/add-device-to-site-modal";
import { contactNameDisplay } from "@/lib/vocabulary";

// ── Types ────────────────────────────────────────────────────────────

interface SiteDetail {
    id: string;
    customer_id: string;
    // #1040 — how to get in. `gate_code` is redacted server-side unless the
    // caller holds access_codes.view; `gate_code_on_file` still says one
    // EXISTS, so a blank never reads as "no code".
    access_notes?: string | null;
    site_hours?: string | null;
    gate_code?: string | null;
    gate_code_on_file?: boolean;
    /** "Needs a PO before we bill" (sites.po_required, 10/17). */
    po_required?: boolean;
    customer_name?: string | null;
    // Payment-profile chip beside "Part of {Customer}" (Location Page V2 item 1).
    customer_payment_profile?: string | null;
    customer_net_terms_days?: number | null;
    customer_has_card_on_file?: boolean | null;
    name: string | null;
    address_line: string | null;
    address_line2: string | null;
    city: string | null;
    region: string | null;
    postal_code: string | null;
    water_provider_id?: string | null;
    water_provider_name: string | null;
    water_provider_display_name?: string | null;
    water_provider_submission_platform: string | null;
    water_provider_is_portal?: boolean | null;
    water_provider_portal_url: string | null;
    water_provider_report_name?: string | null;
    account_number: string | null;
    water_account_number?: string | null;
    asset_count: number;
    // Live location contact (sites.default_contact_id).
    site_contact_id: string | null;
    site_contact_first_name: string | null;
    site_contact_last_name: string | null;
    site_contact_email: string | null;
    site_contact_phone: string | null;
    site_contact_role: string | null;
    site_contact_last_verified_at: string | null;
    // Account/billing fallback (customer primary contact, else customers row).
    account_contact_id: string | null;
    account_contact_first_name: string | null;
    account_contact_last_name: string | null;
    account_contact_email: string | null;
    account_contact_phone: string | null;
    account_contact_role: string | null;
    customer_email: string | null;
    customer_phone: string | null;
    // These have been on the wire since migration 016 and the SiteDetail
    // type simply never declared them, so no UI could reach them. `notes`
    // is the clearest case: the column exists, PATCH accepts it, and
    // `sites/detail.tsx` contained zero occurrences of the word.
    notes?: string | null;
    is_active?: boolean | null;
    // #1042: the site centroid, so the device map can fall back to it. On the
    // wire from SITE_ROW_CORE_SELECT all along; the type never declared it.
    latitude?: number | string | null;
    longitude?: number | string | null;
    phone?: string | null;
    country_code?: string | null;
}

interface ContactOption {
    id: string;
    first_name: string;
    last_name: string | null;
    email: string | null;
    phone: string | null;
    role_title: string | null;
    is_primary: boolean;
    fromServiceTitan?: boolean;
}

// Merged contact-hierarchy read (GET /customers/:id/contact-methods) —
// includes office-authored contacts AND full-fidelity ServiceTitan imports.
interface HierarchyContactApi {
    id: string;
    firstName: string | null;
    lastName: string | null;
    roleTitle: string | null;
    isPrimary: boolean;
    source: string;
    sourceContactId: string | null;
    methods: Array<{ kind: string; value: string; memo: string | null }>;
}

// Adapt a hierarchy contact to the picker's option shape. `id` prefers the
// linked legacy customer_contacts id (so "Current" matching keeps working
// against sites.default_contact_id); ST-only contacts pass their hierarchy
// id — the PATCH /sites/:id/default-contact route reverse-materializes a
// legacy row for them server-side.
function hierarchyToOption(c: HierarchyContactApi): ContactOption {
    return {
        id: c.sourceContactId ?? c.id,
        first_name: c.firstName?.trim() || "—",
        last_name: c.lastName,
        email: c.methods.find((m) => m.kind === "email")?.value ?? null,
        phone: c.methods.find((m) => m.kind === "phone")?.value ?? null,
        role_title: c.roleTitle,
        is_primary: c.isPrimary,
        fromServiceTitan: c.source === "st_import",
    };
}

interface SiteAssetRow {
    id: string;
    name: string;
    size_label: string | null;
    serial_number: string | null;
    manufacturer: string | null;
    model: string | null;
    assembly_type: string | null;
    service_type: string | null;
    service_type_other: string | null;
    meter_number: string | null;
    parent_asset_id: string | null;
    location_description: string | null;
    pipe_material: string | null;
    pipe_material_source: "vision_confirmed" | "human" | "import" | null;
    pipe_material_confidence: "high" | "medium" | "low" | null;
    status: string | null;
}

/**
 * The two tabs a trade filter can actually narrow. Anywhere else the chip
 * strip would be a control that changes nothing on screen.
 */
const TRADE_FILTERABLE_TABS = new Set<LocationTabId>(["devices", "tests"]);

interface SiteSummaryResponse {
    site: SiteDetail;
    trades: TradeSummary[];
    hiddenTrades: Array<{ id: string; code: string; name: string }>;
    /** #1011: the per-site work rollup, riding along on this same round trip. */
    rollup: {
        device_count: number;
        overdue_count: number;
        due_90_count: number;
        missing_due_count: number;
        failed_not_retested_count: number;
        oldest_overdue_date: string | null;
        next_due_date: string | null;
        last_test_at: string | null;
        last_visited_at: string | null;
        open_job_count: number;
        scheduled_job_count: number;
        due_soon_unscheduled_count: number;
        open_estimate_count: number;
        open_estimate_value: string;
        approved_unscheduled_count: number;
        approved_unscheduled_value: string;
        unpaid_invoice_count: number;
        unpaid_balance: string;
        portal_account_missing: boolean;
        never_visited: boolean;
        imported_only: boolean;
        /** #1043: filing status and open tasks, from the same round trip. */
        filing_ready_count: number;
        filing_bounced_count: number;
        open_task_count: number;
        urgent_task_count: number;
    } | null;
}

// Trade code -> short label used in chips. Falls back to `name` when
// a new trade ships without a custom label.
const TRADE_CHIP_LABEL: Record<string, string> = {
    backflow: "Backflow",
    fire_ext: "Fire Ext",
    underground_fire: "U-Fire",
    plumbing: "Plumbing",
    hvac: "HVAC",
};

// ── Page ─────────────────────────────────────────────────────────────

export default function SiteDetailPage() {
    const { siteId } = useParams<{ siteId: string }>();
    const [addOpen, setAddOpen] = useState(false);
    const [reconcileOpen, setReconcileOpen] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [editingAsset, setEditingAsset] = useState<SiteAssetRow | null>(null);
    const [promotedTrades, setPromotedTrades] = useState<TradeSummary[]>([]);

    const [deviceView, setDeviceView] = useState<"table" | "map">("table");

    // Tab + trade filter live in the URL so a link somebody sends opens the
    // same page they were looking at. `parseLocationView` validates both --
    // `customers/detail.tsx:437` reads its tab raw and renders a blank body
    // on a junk value, and that is the bug not to copy here.
    const [searchParams, setSearchParams] = useSearchParams();

    const summaryQ = useQuery({
        queryKey: ["site-summary", siteId],
        queryFn: () => api.get<SiteSummaryResponse>(`/api/sites/${siteId}/summary`),
        enabled: !!siteId,
    });

    const data = summaryQ.data;
    const site = data?.site;

    const mergedTrades = useMemo<TradeSummary[]>(() => {
        const base = data?.trades ?? [];
        // Promoted hidden trades are appended at the end with zero
        // counts so the user can still click into them and start
        // adding devices/jobs.
        if (promotedTrades.length === 0) return base;
        const ids = new Set(base.map((t) => t.id));
        return [...base, ...promotedTrades.filter((p) => !ids.has(p.id))];
    }, [data?.trades, promotedTrades]);

    const hiddenAvailable = useMemo(() => {
        const promotedIds = new Set(promotedTrades.map((t) => t.id));
        return (data?.hiddenTrades ?? []).filter((h) => !promotedIds.has(h.id));
    }, [data?.hiddenTrades, promotedTrades]);

    // `trade-panel.tsx` opened its Tests section automatically when the
    // trade had overdue devices (`defaultOpen={overdue_count > 0}`) -- a
    // recorded do-not-regress rule: a compliance gap is never one click
    // away. With tabs by record type the equivalent is the default ACTIVE
    // TAB, so overdue lands on Tests and everything else lands on Jobs.
    //
    // Read ONCE, on first resolve. If this recomputed on every render a
    // background refetch could yank the user off the tab they just opened.
    const overdueTotal = data?.rollup?.overdue_count ?? 0;
    const landedRef = useRef(false);
    const [landedTab, setLandedTab] = useState<LocationTabId | null>(null);
    useEffect(() => {
        if (landedRef.current || summaryQ.isLoading) return;
        landedRef.current = true;
        setLandedTab(parseLocationView(searchParams, { overdueCount: overdueTotal }).tab);
        // Deliberately not depending on searchParams: this fires once, and
        // afterwards the URL is the only thing that moves the tab.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [summaryQ.isLoading, overdueTotal]);

    const urlView = parseLocationView(searchParams, { overdueCount: overdueTotal });
    const activeTradeCode = urlView.trade;
    const activeTab: LocationTabId = searchParams.get("tab") ? urlView.tab : (landedTab ?? urlView.tab);

    const setView = (next: { tab?: LocationTabId; trade?: string | null }) => {
        setSearchParams(
            (prev) => {
                const merged = locationViewToSearch({
                    tab: next.tab ?? activeTab,
                    trade: next.trade === undefined ? activeTradeCode : next.trade,
                });
                const out = new URLSearchParams(prev);
                out.delete("trade");
                for (const [k, v] of Object.entries(merged)) out.set(k, v);
                return out;
            },
            { replace: true },
        );
    };

    // The map reads the SAME endpoint and the SAME trade filter the table
    // does, so the two views cannot disagree about which devices exist. Only
    // fetched when the map is actually open.
    //
    // Lives ABOVE the loading/not-found early returns on purpose. It used to
    // sit below them, which meant the first (loading) render called one fewer
    // hook than the render after the summary resolved -- React counts hooks,
    // so every cold load of this page died on "Rendered more hooks than during
    // the previous render" (minified error #310) and the user got the crash
    // card instead of the location. `enabled` is what gates the fetch; the
    // hook itself must run on every render.
    const mapDevicesQ = useQuery({
        queryKey: ["site-map-devices", siteId, activeTradeCode],
        queryFn: () => {
            const p = new URLSearchParams({ limit: "500" });
            if (activeTradeCode) p.set("trade", activeTradeCode);
            return api.get<{ assets: MappableDevice[] }>(`/api/sites/${siteId}/assets?${p.toString()}`);
        },
        enabled: !!siteId && deviceView === "map" && activeTab === "devices",
    });
    const mapDevices = mapDevicesQ.data?.assets ?? [];

    if (summaryQ.isLoading || !siteId) {
        return <LocationHubSkeleton />;
    }
    if (!site) {
        return (
            <div className="p-8 text-center text-foreground/80">
                Location not found.{" "}
                <BackLink fallback="/customers" fallbackLabel="Back to customers" className="text-primary underline" />
            </div>
        );
    }

    const address = [
        [site.address_line, site.address_line2].filter(Boolean).join(", "),
        [site.city, site.region].filter(Boolean).join(", "),
        site.postal_code,
    ]
        .filter(Boolean)
        .join(" — ");

    const siteCtx: SiteContext = {
        siteId: site.id,
        customerId: site.customer_id,
        siteName: site.name ?? "",
        waterProviderName: site.water_provider_name,
        waterProviderSubmissionPlatform: site.water_provider_submission_platform,
        // Canonical portal account only — the legacy ST code never renders
        // in a portal-account slot.
        accountNumber: site.water_account_number?.trim() || null,
    };

    // The trade the filter currently names, or null for All. Its summary
    // feeds the Devices/Tests counts; null is a real value, not a missing
    // one, and the sections handle it.
    const activeTradeSummary = activeTradeCode ? (mergedTrades.find((t) => t.code === activeTradeCode) ?? null) : null;
    const testsUnavailable = testsUnavailableReason(mergedTrades.map((t) => t.code));

    const paymentChip = paymentProfileChip(
        site.customer_payment_profile,
        site.customer_net_terms_days,
        site.customer_has_card_on_file,
    );
    const PAYMENT_CHIP_CLASS: Record<string, string> = {
        emerald: "border-emerald-500/50 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
        sky: "border-sky-500/50 bg-sky-500/10 text-sky-700 dark:text-sky-300",
        amber: "border-amber-500/50 bg-amber-500/10 text-amber-700 dark:text-amber-300",
        muted: "border-border text-foreground/80",
    };

    return (
        <div className="mx-auto max-w-7xl px-4 py-5 space-y-5">
            {/* Whole-page drop is handled globally by the UniversalDropLayer in
                AppShell now; a file dropped while on this location auto-files
                here. The old per-page PageDropZone was removed per
                COMMAND-CENTER-REDESIGN.md decision 5. */}
            {/* ── Breadcrumb + identity header ─────────────────────
                Combined into one solid card so it doesn't sit directly
                against the hexagon backdrop, which was washing the
                title out to barely-legible at the high-density
                center of the page. */}
            <Card className="overflow-hidden">
                <CardContent className="p-4 space-y-2.5">
                    <div className="flex items-center gap-2 text-sm">
                        <BackLink
                            fallback={`/customers/${site.customer_id}`}
                            fallbackLabel="Back to customer"
                            className="text-foreground/85 hover:text-primary flex items-center gap-1 font-medium"
                            icon={<ArrowLeft className="h-3.5 w-3.5" />}
                        />
                    </div>
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                        <div className="min-w-0">
                            <h1 className="text-2xl font-bold flex items-center gap-2 text-foreground">
                                <MapPin className="h-5 w-5 text-foreground/80" />
                                {site.name ?? "Unnamed location"}
                            </h1>
                            {/* THE HEADLINE FIX (Location Page V2 item 1): the
                                parent customer, one tap away, with how they
                                pay right beside it. */}
                            <p className="mt-1 flex flex-wrap items-center gap-2 text-sm">
                                <span className="text-foreground/70">Part of</span>
                                <Link
                                    to={`/customers/${site.customer_id}`}
                                    className="font-semibold text-primary hover:underline"
                                >
                                    {site.customer_name ?? "Customer"}
                                </Link>
                                {paymentChip && (
                                    <Badge
                                        variant="outline"
                                        className={cn("text-[10px]", PAYMENT_CHIP_CLASS[paymentChip.tone])}
                                    >
                                        {paymentChip.label}
                                    </Badge>
                                )}
                            </p>
                            {address && <p className="text-sm text-foreground/85 mt-1">{address}</p>}
                            {/* What this location IS, in one sentence, from
                                facts the page already loaded. A CSR picking up
                                the phone should not have to read four cards to
                                answer "what's the story with this address". */}
                            <div className="mt-2">
                                <LocationHeadline rollup={data?.rollup ?? null} siteId={site.id} />
                            </div>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                            <Link
                                to={newJobHref(site.customer_id, site.id)}
                                className={cn(
                                    buttonVariants(),
                                    "bg-primary text-primary-foreground hover:bg-primary/90",
                                )}
                            >
                                <Plus className="h-4 w-4" /> New Job
                            </Link>
                            <Link
                                to={newEstimateHref(site.customer_id, site.id)}
                                className={cn(buttonVariants({ size: "sm", variant: "outline" }))}
                            >
                                <FileText className="h-4 w-4 mr-1" /> New Estimate
                            </Link>
                            <Button size="sm" variant="outline" onClick={() => setAddOpen(true)}>
                                <Plus className="h-4 w-4 mr-1" /> Add device
                            </Button>
                            {reconcileEntryVisible(mergedTrades.length) && (
                                <Button size="sm" variant="outline" onClick={() => setReconcileOpen(true)}>
                                    Reconcile from list
                                </Button>
                            )}
                            <Button size="sm" variant="outline" onClick={() => setEditOpen(true)}>
                                <Pencil className="h-4 w-4 mr-1" /> Edit Location
                            </Button>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* ── Two-up layout (spec item 6): main = work; rail = contacts,
                water, devices summary. Single column on narrow widths. ── */}
            <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_340px] items-start">
                <div className="min-w-0 space-y-5">
                    {/* ── Compliance, always visible ──────────────────
                        Above the tabs on purpose: it used to be a sentence
                        printed INSIDE the Tests card, which meant it
                        disappeared when that collapsible closed, was scoped
                        to one trade, and carried a count with no age. */}
                    <ComplianceCard rollup={data?.rollup ?? null} onAction={(next) => setView(next)} />

                    {/* ── Open tasks at this location ─────────────────
                        `task_links.entity_type = 'site'` has been a valid link
                        since migration 324 and this page never showed it
                        (#1043). Same component the job and customer pages use;
                        it renders nothing when there are no open tasks, so an
                        ordinary location gains no empty box. The compliance
                        card's "See tasks" row scrolls to this id. */}
                    <div id="site-tasks" className="scroll-mt-24">
                        <TaskListInline entityType="site" entityId={site.id} />
                    </div>

                    {/* Add from the field: what techs added for this location.
                        Never writes over the Location notes box. */}
                    <FromTheFieldSection kind="location" id={site.id} />

                    {/* ── Trade FILTER, not the tab axis ───────────────
                        Only shown on the two tabs it can filter. On Jobs,
                        Estimates, Invoices, Documents and Photos it would be a
                        control that changes nothing, which is worse than no
                        control at all. */}
                    {TRADE_FILTERABLE_TABS.has(activeTab) && (
                        <TradeChipStrip
                            trades={mergedTrades}
                            hiddenTrades={hiddenAvailable}
                            activeCode={activeTradeCode}
                            onSelectChip={(code) => setView({ trade: code })}
                            onPromote={(t) =>
                                setPromotedTrades((prev) =>
                                    prev.some((p) => p.id === t.id)
                                        ? prev
                                        : [
                                              ...prev,
                                              {
                                                  id: t.id,
                                                  code: t.code,
                                                  name: t.name,
                                                  device_count: 0,
                                                  open_job_count: 0,
                                                  job_count: 0,
                                                  overdue_count: 0,
                                                  due_soon_count: 0,
                                                  attachment_count: 0,
                                                  last_inspection_at: null,
                                                  next_due_date: null,
                                              },
                                          ],
                                )
                            }
                        />
                    )}

                    {/* ── Tabs are RECORD TYPES now, not trades ───────
                        Every tab stays reachable even on a site with no
                        service lines at all, so an empty location can still
                        receive a document or a photo. The empty state sits
                        above them rather than replacing them. */}
                    {mergedTrades.length === 0 && (
                        <EmptyLocationState
                            onAddDevice={() => setAddOpen(true)}
                            onReconcile={() => setReconcileOpen(true)}
                        />
                    )}
                    <Tabs value={activeTab} onValueChange={(v) => setView({ tab: v as LocationTabId })}>
                        <TabsList className="flex w-full justify-start overflow-x-auto">
                            {LOCATION_TABS.map((t) => (
                                <TabsTrigger key={t.id} value={t.id} className="gap-2">
                                    {t.label}
                                    {/* The per-trade overdue badge used to live on
                                        the trade tab trigger. With trades gone from
                                        the axis it has no home, so it is promoted:
                                        the SITE total lands here, and the per-trade
                                        counts stay on the filter chips. The strip
                                        still warns you before you click. */}
                                    {t.id === "tests" && overdueTotal > 0 && (
                                        <Badge
                                            variant="outline"
                                            className="ml-1 h-4 border-destructive/40 bg-destructive/10 px-1 text-[10px] text-destructive"
                                        >
                                            {overdueTotal}
                                        </Badge>
                                    )}
                                </TabsTrigger>
                            ))}
                        </TabsList>

                        <TabsContent value="jobs" className="mt-4">
                            <JobsSection
                                site={siteCtx}
                                tradeId={activeTradeSummary?.id ?? null}
                                tradeCode={activeTradeCode}
                            />
                        </TabsContent>
                        <TabsContent value="estimates" className="mt-4">
                            <EstimatesSection site={siteCtx} />
                        </TabsContent>
                        <TabsContent value="invoices" className="mt-4">
                            <InvoicesSection site={siteCtx} />
                        </TabsContent>
                        <TabsContent value="devices" className="mt-4 space-y-4">
                            {/* The per-trade KPI strip. It only appears when a
                                trade filter is ACTIVE, because its numbers are
                                per-trade — showing it under "All trades" would
                                mean a strip labelled with one trade's figures
                                over a list of every trade's devices. The
                                site-wide equivalent is the header sentence and
                                the compliance card, which are always there. */}
                            {activeTradeSummary && (
                                <TradeContextStrip
                                    site={siteCtx}
                                    tradeCode={activeTradeSummary.code}
                                    summary={activeTradeSummary}
                                />
                            )}
                            {/* #1042: table or map, same devices, same filter.
                                A toggle rather than a tab — "where is it" is
                                a VIEW of the device list, not a different
                                list, and splitting them would let the two
                                disagree about which devices exist. */}
                            <div className="flex justify-end">
                                <div className="inline-flex rounded-md border border-border bg-card p-0.5 text-xs">
                                    {(["table", "map"] as const).map((v) => (
                                        <button
                                            key={v}
                                            type="button"
                                            onClick={() => setDeviceView(v)}
                                            className={cn(
                                                "rounded px-3 py-1 font-medium capitalize transition",
                                                deviceView === v
                                                    ? "bg-primary text-primary-foreground"
                                                    : "text-foreground/75 hover:text-foreground",
                                            )}
                                        >
                                            {v}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            {deviceView === "map" ? (
                                <DeviceMapView devices={mapDevices} site={site} />
                            ) : (
                                <DevicesSection site={siteCtx} trade={activeTradeCode} summary={activeTradeSummary} />
                            )}
                        </TabsContent>
                        <TabsContent value="tests" className="mt-4">
                            {/* A hidden tab and an empty tab tell the office the
                                same nothing. When the site has no testable trade
                                we say WHY instead of rendering a zero-state that
                                implies tests are coming. */}
                            {testsUnavailable ? (
                                <Card>
                                    <CardContent className="py-10 text-center text-sm text-foreground/75">
                                        {testsUnavailable}
                                    </CardContent>
                                </Card>
                            ) : (
                                <TestsSection
                                    site={siteCtx}
                                    trade={activeTradeCode}
                                    summary={activeTradeSummary}
                                    siteOverdueCount={overdueTotal}
                                />
                            )}
                        </TabsContent>
                        <TabsContent value="documents" className="mt-4">
                            <DocumentsSection site={siteCtx} />
                        </TabsContent>
                        <TabsContent value="photos" className="mt-4">
                            <PhotosSection site={siteCtx} />
                        </TabsContent>
                    </Tabs>
                </div>

                {/* ── Rail: contacts, water & reporting, devices summary ── */}
                <div className="space-y-5">
                    {/* #1040: "Getting in" earns rail space above the contact
                        card, because it is what the office reads out loud when
                        a tech calls from the parking lot. Rendered only when
                        there is something to say — an empty card on every
                        location would train people to skip the whole rail. */}
                    {(site.access_notes || site.site_hours || site.gate_code || site.gate_code_on_file) && (
                        <Card>
                            <CardContent className="space-y-2 p-4">
                                <h3 className="flex items-center gap-2 text-sm font-semibold">
                                    <KeyRound className="h-4 w-4 text-foreground/80" /> Getting in
                                </h3>
                                {site.access_notes && (
                                    <p className="whitespace-pre-wrap text-sm text-foreground/85">
                                        {site.access_notes}
                                    </p>
                                )}
                                {site.site_hours && (
                                    <p className="text-sm">
                                        <span className="text-foreground/70">Hours: </span>
                                        {site.site_hours}
                                    </p>
                                )}
                                {site.gate_code ? (
                                    <p className="text-sm">
                                        <span className="text-foreground/70">Gate code: </span>
                                        <span className="font-mono font-semibold">{site.gate_code}</span>
                                    </p>
                                ) : site.gate_code_on_file ? (
                                    // Redacted, not absent. Saying nothing here
                                    // would send someone to a gate believing
                                    // there is no code to ask for.
                                    <p className="text-sm text-foreground/70">
                                        A gate code is on file — you don&apos;t have permission to see it.
                                    </p>
                                ) : null}
                            </CardContent>
                        </Card>
                    )}
                    <SiteContactCard site={site} />
                    <SitePoRequiredCard site={site} />
                    <SiteWaterReportingCard site={site} />
                    {/* The `> 1 trade` gate was justified by "with one trade it
                        just repeats the chip strip + KPI tiles already on
                        screen". Neither is on screen from every tab any more --
                        the chips only appear on Devices and Tests -- so the rail
                        earns its space at one trade too. Clicking a trade here
                        now sets the FILTER and lands on Devices. */}
                    {mergedTrades.length >= 1 && (
                        <DevicesSummaryRail
                            trades={mergedTrades}
                            onOpenTab={(code) => setView({ tab: "devices", trade: code })}
                        />
                    )}
                </div>
            </div>

            {addOpen && siteId && <AddDeviceToSiteModal siteId={siteId} onClose={() => setAddOpen(false)} />}
            {reconcileOpen && siteId && (
                <ReconcileFromListDialog siteId={siteId} open={reconcileOpen} onClose={() => setReconcileOpen(false)} />
            )}
            {editOpen && siteId && <EditSiteModal siteId={siteId} site={site} onClose={() => setEditOpen(false)} />}
            {editingAsset && siteId && (
                <EditSiteDeviceModal siteId={siteId} asset={editingAsset} onClose={() => setEditingAsset(null)} />
            )}
        </div>
    );
}

// The old "Location documents card" (SiteAttachmentRow, formatDocDate,
// SOURCE_LABEL, SiteDocumentsCard) lived here and read `/site-photos` --
// `site_attachments` ALONE. It could not see a single file a tech uploaded
// on a job or against a device. Replaced by `location/documents-section.tsx`
// on `GET /api/sites/:siteId/documents`, which unions all three attachment
// tables and dedupes them. Its drag-out-to-Gmail and download affordances
// were carried over rather than lost.

// ── Location contact card ────────────────────────────────────────────

function fullName(first: string | null | undefined, last: string | null | undefined): string {
    // Routed through the shared guard: the import umbrella ("ServiceTitan
    // contacts") is a bucket of numbers, not a person, and this helper feeds
    // BOTH the site contact and the account-contact fallback. #824 fixed this
    // on the job page only; #1243 found it still reading as a name here.
    return contactNameDisplay(first, last).text;
}

/** Admin, Office and Manager may change "Needs a PO before we bill" (server enforces it too). */
export function canSetPoRequired(role: string | null | undefined): boolean {
    const r = (role ?? "").trim().toLowerCase();
    return r === "admin" || r === "office" || r === "manager";
}

/**
 * "Needs a PO before we bill" (10/17). When it is on and a job here has no PO
 * number, Closeout's holder line reads "Waiting on customer — PO" and the
 * card's button is "Add PO #". Admin, Office and Manager get the checkbox;
 * everyone else only sees it when it is on.
 */
function SitePoRequiredCard({ site }: { site: SiteDetail }) {
    const qc = useQueryClient();
    const { user } = useAuth();
    const canEdit = canSetPoRequired(user?.role);
    const on = site.po_required === true;
    const save = useMutation({
        mutationFn: (next: boolean) => api.patch(`/api/sites/${site.id}`, { poRequired: next }),
        onSuccess: (_r, next) => {
            qc.invalidateQueries({ queryKey: ["site-summary", site.id] });
            toast.success(next ? "This location needs a PO before we bill" : "PO no longer needed here");
        },
        onError: () => toast.error("Couldn't save. Please try again."),
    });
    if (!canEdit && !on) return null;
    return (
        <Card>
            <CardContent className="p-4">
                {canEdit ? (
                    <label className="flex cursor-pointer items-start gap-2.5 text-sm" data-testid="site-po-required">
                        <input
                            type="checkbox"
                            className="mt-0.5 h-4 w-4 accent-primary"
                            checked={on}
                            disabled={save.isPending}
                            onChange={(e) => save.mutate(e.target.checked)}
                        />
                        <span>
                            <span className="font-semibold">Needs a PO before we bill</span>
                            <span className="block text-xs text-foreground/70">
                                Jobs here without a PO number wait on the customer in Closeout.
                            </span>
                        </span>
                    </label>
                ) : (
                    <p className="text-sm font-semibold" data-testid="site-po-required-readonly">
                        Needs a PO before we bill
                    </p>
                )}
            </CardContent>
        </Card>
    );
}

function SiteContactCard({ site }: { site: SiteDetail }) {
    const qc = useQueryClient();
    const [manageOpen, setManageOpen] = useState(false);
    const [editOpen, setEditOpen] = useState(false);

    const hasSiteContact = !!site.site_contact_id;
    const siteName = fullName(site.site_contact_first_name, site.site_contact_last_name);
    const contactStale = isContactStale(site.site_contact_last_verified_at);

    const verifyContact = useMutation({
        mutationFn: () => api.post(`/api/customers/${site.customer_id}/contacts/${site.site_contact_id}/verify`, {}),
        onSuccess: () => {
            toast.success("Contact confirmed as current");
            qc.invalidateQueries({ queryKey: ["site-summary", site.id] });
        },
        onError: () => toast.error("Couldn't confirm the contact — please try again"),
    });

    // Fallback chain when the location has no contact of its own: the
    // customer's primary contact, else the customer record's own email/phone.
    // Always rendered with a "not on-site" label so staff never mistake the
    // maintenance-group / billing contact for the site owner.
    const accountName = fullName(site.account_contact_first_name, site.account_contact_last_name);
    const fallbackName = accountName || site.customer_name || "Account contact";
    const fallbackPhone = site.account_contact_phone || site.customer_phone;
    const fallbackEmail = site.account_contact_email || site.customer_email;

    return (
        <Card>
            <CardContent className="p-5">
                <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                        <div className="flex items-center gap-2 text-sm font-semibold text-foreground/90">
                            <User className="h-4 w-4 text-foreground/70" /> Location contact
                        </div>

                        {hasSiteContact ? (
                            <div className="mt-2 space-y-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-medium">{siteName || "Unnamed contact"}</span>
                                    {site.site_contact_role && (
                                        <Badge variant="secondary" className="text-[10px]">
                                            {site.site_contact_role}
                                        </Badge>
                                    )}
                                </div>
                                <ContactLines phone={site.site_contact_phone} email={site.site_contact_email} />
                                <div className="flex items-center gap-2 pt-1">
                                    <span
                                        className={cn(
                                            "text-xs",
                                            contactStale ? "text-amber-600 dark:text-amber-400" : "text-foreground/60",
                                        )}
                                    >
                                        {contactStale && <AlertCircle className="inline h-3 w-3 mr-1 -mt-0.5" />}
                                        {formatVerifiedLabel(site.site_contact_last_verified_at)}
                                    </span>
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        className="h-6 px-2 text-xs"
                                        disabled={verifyContact.isPending}
                                        onClick={() => verifyContact.mutate()}
                                    >
                                        <ShieldCheck className="h-3.5 w-3.5 mr-1" /> Confirm Still Current
                                    </Button>
                                </div>
                            </div>
                        ) : fallbackPhone || fallbackEmail || accountName ? (
                            <div className="mt-2 space-y-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-medium">{fallbackName}</span>
                                    <Badge
                                        variant="outline"
                                        className="text-[10px] border-amber-400/50 text-amber-600 bg-amber-500/10"
                                    >
                                        Account contact — not on-site
                                    </Badge>
                                </div>
                                <ContactLines phone={fallbackPhone} email={fallbackEmail} />
                                <p className="text-xs text-foreground/70 mt-1">
                                    No on-site contact is set for this location yet.
                                </p>
                            </div>
                        ) : (
                            <p className="mt-2 text-sm text-foreground/70">No location contact set.</p>
                        )}
                    </div>

                    <div className="flex flex-col gap-2 shrink-0">
                        <Button size="sm" variant="outline" onClick={() => setManageOpen(true)}>
                            {hasSiteContact ? "Change" : "Set contact"}
                        </Button>
                        {hasSiteContact && (
                            <Button size="sm" variant="ghost" onClick={() => setEditOpen(true)}>
                                <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
                            </Button>
                        )}
                    </div>
                </div>
            </CardContent>

            {manageOpen && (
                <ManageSiteContactModal
                    siteId={site.id}
                    customerId={site.customer_id}
                    currentContactId={site.site_contact_id}
                    onClose={() => setManageOpen(false)}
                />
            )}
            {editOpen && hasSiteContact && (
                <EditSiteContactModal
                    siteId={site.id}
                    customerId={site.customer_id}
                    contact={{
                        id: site.site_contact_id as string,
                        first_name: site.site_contact_first_name ?? "",
                        last_name: site.site_contact_last_name,
                        email: site.site_contact_email,
                        phone: site.site_contact_phone,
                        role_title: site.site_contact_role,
                    }}
                    onClose={() => setEditOpen(false)}
                />
            )}
        </Card>
    );
}

function ContactLines({ phone, email }: { phone: string | null | undefined; email: string | null | undefined }) {
    return (
        <div className="flex flex-col gap-0.5 text-sm">
            {phone && (
                <a href={`tel:${phone.replace(/\D/g, "")}`} className="flex items-center gap-1.5 text-primary">
                    <Phone className="h-3.5 w-3.5" /> {phone}
                </a>
            )}
            {email && (
                <a href={`mailto:${email}`} className="flex items-center gap-1.5 text-primary break-all">
                    <Mail className="h-3.5 w-3.5" /> {email}
                </a>
            )}
        </div>
    );
}

function ManageSiteContactModal({
    siteId,
    customerId,
    currentContactId,
    onClose,
}: {
    siteId: string;
    customerId: string;
    currentContactId: string | null;
    onClose: () => void;
}) {
    const qc = useQueryClient();
    const [search, setSearch] = useState("");
    const [adding, setAdding] = useState(false);

    // Merged view: manual contacts + ServiceTitan imports (read flip,
    // ST-CONTACTS-IMPORT.md §5). Key kept under "customer-contacts" so the
    // existing invalidations refresh it.
    const contactsQ = useQuery({
        queryKey: ["customer-contacts", customerId, "methods"],
        queryFn: () => api.get<{ contacts: HierarchyContactApi[] }>(`/api/customers/${customerId}/contact-methods`),
        enabled: !!customerId,
    });
    const contacts = (contactsQ.data?.contacts ?? []).map(hierarchyToOption);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        const digits = search.replace(/\D/g, "");
        if (!q) return contacts;
        return contacts.filter((c) => {
            const name = fullName(c.first_name, c.last_name).toLowerCase();
            const role = (c.role_title ?? "").toLowerCase();
            const email = (c.email ?? "").toLowerCase();
            const phoneDigits = (c.phone ?? "").replace(/\D/g, "");
            return (
                name.includes(q) ||
                role.includes(q) ||
                email.includes(q) ||
                (digits.length > 0 && phoneDigits.includes(digits))
            );
        });
    }, [contacts, search]);

    const setDefault = useMutation({
        mutationFn: (contactId: string | null) => api.patch(`/api/sites/${siteId}/default-contact`, { contactId }),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["site-summary", siteId] });
            toast.success("Location contact updated");
            onClose();
        },
        onError: () => toast.error("Couldn't update the location contact"),
    });

    return (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
            <div
                className="w-full max-w-lg rounded-lg border bg-card shadow-xl max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between border-b px-4 py-3">
                    <h3 className="text-sm font-semibold">Location contact</h3>
                    <button type="button" onClick={onClose} className="text-foreground/80 hover:text-foreground">
                        <X className="h-4 w-4" />
                    </button>
                </div>

                <div className="p-4 space-y-3">
                    {adding ? (
                        <AddSiteContactForm
                            siteId={siteId}
                            customerId={customerId}
                            onCancel={() => setAdding(false)}
                            onSaved={() => {
                                qc.invalidateQueries({ queryKey: ["site-summary", siteId] });
                                qc.invalidateQueries({ queryKey: ["customer-contacts", customerId] });
                                toast.success("Contact added and set for this location");
                                onClose();
                            }}
                        />
                    ) : (
                        <>
                            <div className="relative">
                                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-foreground/50" />
                                <Input
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    placeholder="Search contacts by name, phone, email…"
                                    className="pl-8"
                                    autoFocus
                                />
                            </div>

                            <div className="rounded-md border divide-y max-h-72 overflow-y-auto">
                                <button
                                    type="button"
                                    onClick={() => setDefault.mutate(null)}
                                    disabled={setDefault.isPending || !currentContactId}
                                    className="w-full text-left px-3 py-2 text-sm hover:bg-accent disabled:opacity-40"
                                >
                                    <span className="text-muted-foreground">— No location contact —</span>
                                </button>
                                {contactsQ.isLoading ? (
                                    <div className="px-3 py-4 text-sm text-foreground/60">Loading…</div>
                                ) : filtered.length === 0 ? (
                                    <div className="px-3 py-4 text-sm text-foreground/60">No matching contacts.</div>
                                ) : (
                                    filtered.map((c) => {
                                        const active = c.id === currentContactId;
                                        return (
                                            <button
                                                key={c.id}
                                                type="button"
                                                onClick={() => setDefault.mutate(c.id)}
                                                disabled={setDefault.isPending}
                                                className={cn(
                                                    "w-full text-left px-3 py-2 text-sm hover:bg-accent",
                                                    active && "bg-primary/10",
                                                )}
                                            >
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <span className="font-medium">
                                                        {fullName(c.first_name, c.last_name) || "Unnamed"}
                                                    </span>
                                                    {c.role_title && (
                                                        <Badge variant="secondary" className="text-[10px]">
                                                            {c.role_title}
                                                        </Badge>
                                                    )}
                                                    {c.fromServiceTitan && (
                                                        <Badge variant="secondary" className="text-[10px]">
                                                            imported
                                                        </Badge>
                                                    )}
                                                    {active && (
                                                        <Badge variant="outline" className="text-[10px]">
                                                            Current
                                                        </Badge>
                                                    )}
                                                </div>
                                                <div className="text-xs text-foreground/70">
                                                    {[c.phone, c.email].filter(Boolean).join(" · ")}
                                                </div>
                                            </button>
                                        );
                                    })
                                )}
                            </div>

                            <Button size="sm" variant="outline" className="w-full" onClick={() => setAdding(true)}>
                                <Plus className="h-4 w-4 mr-1" /> Add new contact
                            </Button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

function AddSiteContactForm({
    siteId,
    customerId,
    onCancel,
    onSaved,
}: {
    siteId: string;
    customerId: string;
    onCancel: () => void;
    onSaved: () => void;
}) {
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [phone, setPhone] = useState("");
    const [email, setEmail] = useState("");
    const [roleTitle, setRoleTitle] = useState("");

    const create = useMutation({
        mutationFn: () =>
            api.post(`/api/customers/${customerId}/contacts`, {
                firstName: firstName.trim(),
                lastName: lastName.trim() || undefined,
                phone: phone.trim() || undefined,
                email: email.trim() || undefined,
                roleTitle: roleTitle.trim() || undefined,
                // Attach the new contact to this location immediately.
                defaultForSiteId: siteId,
            }),
        onSuccess: onSaved,
        onError: () => toast.error("Couldn't create the contact"),
    });

    return (
        <form
            className="space-y-3"
            onSubmit={(e) => {
                e.preventDefault();
                if (!firstName.trim()) {
                    toast.error("First name is required");
                    return;
                }
                create.mutate();
            }}
        >
            <div className="grid grid-cols-2 gap-3">
                <div>
                    <label className="text-xs font-medium">First name</label>
                    <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} autoFocus />
                </div>
                <div>
                    <label className="text-xs font-medium">Last name</label>
                    <Input value={lastName} onChange={(e) => setLastName(e.target.value)} />
                </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
                <div>
                    <label className="text-xs font-medium">Phone</label>
                    <Input value={phone} onChange={(e) => setPhone(e.target.value)} />
                </div>
                <div>
                    <label className="text-xs font-medium">Email</label>
                    <Input value={email} onChange={(e) => setEmail(e.target.value)} />
                </div>
            </div>
            <div>
                <label className="text-xs font-medium">Role / title</label>
                <Input
                    value={roleTitle}
                    onChange={(e) => setRoleTitle(e.target.value)}
                    placeholder="e.g. Store manager"
                />
            </div>
            <div className="flex justify-end gap-2 pt-1">
                <Button type="button" size="sm" variant="outline" onClick={onCancel}>
                    Back
                </Button>
                <Button type="submit" size="sm" disabled={create.isPending}>
                    {create.isPending ? "Saving…" : "Add & set as contact"}
                </Button>
            </div>
        </form>
    );
}

function EditSiteContactModal({
    siteId,
    customerId,
    contact,
    onClose,
}: {
    siteId: string;
    customerId: string;
    contact: {
        id: string;
        first_name: string;
        last_name: string | null;
        email: string | null;
        phone: string | null;
        role_title: string | null;
    };
    onClose: () => void;
}) {
    const qc = useQueryClient();
    const [firstName, setFirstName] = useState(contact.first_name ?? "");
    const [lastName, setLastName] = useState(contact.last_name ?? "");
    const [phone, setPhone] = useState(contact.phone ?? "");
    const [email, setEmail] = useState(contact.email ?? "");
    const [roleTitle, setRoleTitle] = useState(contact.role_title ?? "");

    const save = useMutation({
        mutationFn: () =>
            api.patch(`/api/customers/${customerId}/contacts/${contact.id}`, {
                firstName: firstName.trim(),
                lastName: lastName.trim() || null,
                phone: phone.trim() || null,
                email: email.trim() || null,
                roleTitle: roleTitle.trim() || null,
            }),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["site-summary", siteId] });
            qc.invalidateQueries({ queryKey: ["customer-contacts", customerId] });
            toast.success("Contact updated");
            onClose();
        },
        onError: () => toast.error("Couldn't update the contact"),
    });

    return (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
            <div className="w-full max-w-md rounded-lg border bg-card shadow-xl" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between border-b px-4 py-3">
                    <h3 className="text-sm font-semibold">Edit contact</h3>
                    <button type="button" onClick={onClose} className="text-foreground/80 hover:text-foreground">
                        <X className="h-4 w-4" />
                    </button>
                </div>
                <form
                    className="p-4 space-y-3"
                    onSubmit={(e) => {
                        e.preventDefault();
                        if (!firstName.trim()) {
                            toast.error("First name is required");
                            return;
                        }
                        save.mutate();
                    }}
                >
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-xs font-medium">First name</label>
                            <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} />
                        </div>
                        <div>
                            <label className="text-xs font-medium">Last name</label>
                            <Input value={lastName} onChange={(e) => setLastName(e.target.value)} />
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-xs font-medium">Phone</label>
                            <Input value={phone} onChange={(e) => setPhone(e.target.value)} />
                        </div>
                        <div>
                            <label className="text-xs font-medium">Email</label>
                            <Input value={email} onChange={(e) => setEmail(e.target.value)} />
                        </div>
                    </div>
                    <div>
                        <label className="text-xs font-medium">Role / title</label>
                        <Input value={roleTitle} onChange={(e) => setRoleTitle(e.target.value)} />
                    </div>
                    <div className="flex justify-end gap-2 pt-1">
                        <Button type="button" size="sm" variant="outline" onClick={onClose}>
                            Cancel
                        </Button>
                        <Button type="submit" size="sm" disabled={save.isPending}>
                            {save.isPending ? "Saving…" : "Save changes"}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
}

// ── Trade chip strip ─────────────────────────────────────────────────

function TradeChipStrip({
    trades,
    hiddenTrades,
    activeCode,
    onSelectChip,
    onPromote,
}: {
    trades: TradeSummary[];
    hiddenTrades: Array<{ id: string; code: string; name: string }>;
    activeCode: string | null;
    onSelectChip: (code: string | null) => void;
    onPromote: (t: { id: string; code: string; name: string }) => void;
}) {
    const totalDevices = trades.reduce((n, t) => n + t.device_count, 0);
    const totalOverdue = trades.reduce((n, t) => n + t.overdue_count, 0);
    return (
        <div className="flex flex-wrap items-center gap-2">
            {/* "All" is an explicit chip, not the absence of one. It writes NO
                `trade` param at all — `trade=all` would make the server look
                for a trade with that code and return nothing — and it is what
                keeps untagged legacy imports visible, since the server-side
                grandfathering only runs when it decides the filter. */}
            <button
                type="button"
                onClick={() => onSelectChip(null)}
                className={cn(
                    "inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs shadow-sm backdrop-blur-sm transition",
                    activeCode === null
                        ? "border-primary bg-primary text-primary-foreground"
                        : "border-border bg-card text-foreground hover:border-primary/60 hover:bg-primary/10",
                )}
            >
                <span className="font-semibold">All trades</span>
                <span
                    className={cn(
                        "tabular-nums",
                        activeCode === null ? "text-primary-foreground/85" : "text-foreground/85",
                    )}
                >
                    {totalDevices} device{totalDevices === 1 ? "" : "s"}
                </span>
                {totalOverdue > 0 && (
                    <span
                        className={cn(
                            "inline-flex items-center gap-0.5 font-medium",
                            activeCode === null ? "text-primary-foreground" : "text-destructive",
                        )}
                    >
                        <AlertCircle className="h-3 w-3" /> {totalOverdue}
                    </span>
                )}
            </button>
            {trades.map((t) => {
                const active = activeCode === t.code;
                const label = TRADE_CHIP_LABEL[t.code] ?? t.name;
                return (
                    <button
                        key={t.id}
                        type="button"
                        onClick={() => onSelectChip(t.code)}
                        className={cn(
                            // Active chip mirrors the highlighted tab
                            // below — solid primary background and
                            // primary-foreground text — so the user's
                            // place in the page is obvious. Inactive
                            // chips stay solid card so they don't get
                            // washed out by the hexagon backdrop.
                            "inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs shadow-sm transition backdrop-blur-sm",
                            active
                                ? "border-primary bg-primary text-primary-foreground"
                                : "border-border bg-card text-foreground hover:border-primary/60 hover:bg-primary/10",
                        )}
                    >
                        <span className="font-semibold">{label}</span>
                        <span
                            className={cn("tabular-nums", active ? "text-primary-foreground/85" : "text-foreground/85")}
                        >
                            {t.device_count} device{t.device_count === 1 ? "" : "s"}
                        </span>
                        {t.overdue_count > 0 && (
                            <span
                                className={cn(
                                    "inline-flex items-center gap-0.5 font-medium",
                                    active ? "text-primary-foreground" : "text-destructive",
                                )}
                            >
                                <AlertCircle className="h-3 w-3" /> {t.overdue_count}
                            </span>
                        )}
                    </button>
                );
            })}

            {hiddenTrades.length > 0 && (
                <Popover>
                    <PopoverTrigger asChild>
                        <button
                            type="button"
                            className="inline-flex items-center gap-1 rounded-full border border-dashed bg-card px-3 py-1.5 text-xs text-foreground hover:border-primary hover:text-primary"
                        >
                            <Plus className="h-3 w-3" /> Add service line
                        </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-56 p-1">
                        <div className="text-[11px] text-foreground/80 px-2 py-1.5 flex items-center gap-1">
                            <Sparkles className="h-3 w-3" /> Show another trade
                        </div>
                        {hiddenTrades.map((t) => (
                            <button
                                key={t.id}
                                type="button"
                                onClick={() => onPromote(t)}
                                className="w-full text-left px-2 py-1.5 text-sm rounded-md hover:bg-accent"
                            >
                                {TRADE_CHIP_LABEL[t.code] ?? t.name}
                            </button>
                        ))}
                    </PopoverContent>
                </Popover>
            )}
        </div>
    );
}

// ── Empty state for sites with no signal ─────────────────────────────

function EmptyLocationState({ onAddDevice, onReconcile }: { onAddDevice: () => void; onReconcile?: () => void }) {
    return (
        <Card>
            <CardContent className="py-12 text-center space-y-4">
                <div className="mx-auto h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <Sparkles className="h-6 w-6 text-primary" />
                </div>
                <div>
                    <p className="text-lg font-medium">No service lines tracked here yet.</p>
                    <p className="text-sm text-foreground/80 mt-1">
                        Add the first device — backflow, fire extinguisher, anything you service — and this location
                        starts wiring itself up. Got a BSI/city device list? Paste it with Reconcile from list.
                    </p>
                </div>
                <div className="flex items-center justify-center gap-2 flex-wrap">
                    <Button onClick={onAddDevice}>
                        <Plus className="h-4 w-4 mr-1" /> Add first device
                    </Button>
                    {onReconcile && (
                        <Button variant="outline" onClick={onReconcile}>
                            Reconcile from list
                        </Button>
                    )}
                </div>
            </CardContent>
        </Card>
    );
}

// ── Devices summary rail (Location Page V2 item 6) ───────────────────

/**
 * Compact per-trade device rollup for the rail: counts + overdue, one tap
 * to the matching service-line tab in the main column.
 */
function DevicesSummaryRail({ trades, onOpenTab }: { trades: TradeSummary[]; onOpenTab: (code: string) => void }) {
    if (trades.length === 0) return null;
    return (
        <Card>
            <CardContent className="p-5 space-y-3">
                <h2 className="text-sm font-semibold">Devices</h2>
                <ul className="divide-y divide-border rounded-md border border-border">
                    {trades.map((t) => (
                        <li key={t.id}>
                            <button
                                type="button"
                                onClick={() => onOpenTab(t.code)}
                                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-muted/40"
                            >
                                <span className="min-w-0 flex-1 truncate font-medium">
                                    {TRADE_CHIP_LABEL[t.code] ?? t.name}
                                </span>
                                <span className="shrink-0 tabular-nums text-foreground/80">
                                    {t.device_count} device{t.device_count === 1 ? "" : "s"}
                                </span>
                                {t.overdue_count > 0 && (
                                    <Badge
                                        variant="outline"
                                        className="shrink-0 border-destructive/40 bg-destructive/10 text-[10px] text-destructive"
                                    >
                                        {t.overdue_count} overdue
                                    </Badge>
                                )}
                            </button>
                        </li>
                    ))}
                </ul>
            </CardContent>
        </Card>
    );
}

// ── Loading skeleton ─────────────────────────────────────────────────

function LocationHubSkeleton() {
    return (
        <div className="mx-auto max-w-6xl px-4 py-6 space-y-6">
            <Skeleton className="h-4 w-32" />
            <div className="flex justify-between items-start">
                <div className="space-y-2 flex-1">
                    <Skeleton className="h-7 w-64" />
                    <Skeleton className="h-4 w-80" />
                </div>
                <Skeleton className="h-10 w-28" />
            </div>
            <div className="flex gap-2">
                <Skeleton className="h-7 w-24 rounded-full" />
                <Skeleton className="h-7 w-24 rounded-full" />
                <Skeleton className="h-7 w-32 rounded-full" />
            </div>
            <Skeleton className="h-96 w-full" />
        </div>
    );
}

// ── Add Device Modal (unchanged from prior page) ─────────────────────

function EditSiteModal({ siteId, site, onClose }: { siteId: string; site: SiteDetail; onClose: () => void }) {
    const qc = useQueryClient();

    const [name, setName] = useState(site.name ?? "");
    const [addressLine, setAddressLine] = useState(site.address_line ?? "");
    const [addressLine2, setAddressLine2] = useState(site.address_line2 ?? "");
    const [city, setCity] = useState(site.city ?? "");
    const [region, setRegion] = useState(site.region ?? "");
    const [postalCode, setPostalCode] = useState(site.postal_code ?? "");
    const [phone, setPhone] = useState(site.phone ?? "");
    const [accountNumber, setAccountNumber] = useState(site.water_account_number ?? "");
    const [notes, setNotes] = useState(site.notes ?? "");
    const [isActive, setIsActive] = useState(site.is_active !== false);
    const [accessNotes, setAccessNotes] = useState(site.access_notes ?? "");
    const [siteHours, setSiteHours] = useState(site.site_hours ?? "");
    const [gateCode, setGateCode] = useState(site.gate_code ?? "");
    const { isAdmin, can } = useAuth();
    const canSeeCodes = isAdmin || can("access_codes.view");

    const save = useMutation({
        mutationFn: () =>
            api.patch(`/api/sites/${siteId}`, {
                name: name.trim() || null,
                addressLine: addressLine.trim() || null,
                addressLine2: addressLine2.trim() || null,
                city: city.trim() || null,
                region: region.trim() || null,
                postalCode: postalCode.trim() || null,
                phone: phone.trim() || null,
                waterAccountNumber: accountNumber.trim() || null,
                notes: notes.trim() || null,
                isActive,
                accessNotes: accessNotes.trim() || null,
                siteHours: siteHours.trim() || null,
                // Only sent when this user may see codes. Sending an empty
                // string from a UI that never showed the current value would
                // silently WIPE it.
                ...(canSeeCodes ? { gateCode: gateCode.trim() || null } : {}),
            }),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["site-summary", siteId] });
            // The rollup's `portal_account_missing` and the compliance card
            // both key off the account number, so they have to refetch too.
            qc.invalidateQueries({ queryKey: ["site-work-summary", siteId] });
            toast.success("Location updated");
            onClose();
        },
        onError: (e) => toast.error(deviceErrorMessage(e, "Couldn't update location")),
    });

    return (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
            <div
                className="w-full max-w-lg rounded-lg border bg-card shadow-xl max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between border-b px-4 py-3">
                    <h3 className="text-sm font-semibold">Edit location</h3>
                    <button type="button" onClick={onClose} className="text-foreground/80 hover:text-foreground">
                        <X className="h-4 w-4" />
                    </button>
                </div>

                <form
                    className="p-4 space-y-4"
                    onSubmit={(e) => {
                        e.preventDefault();
                        save.mutate();
                    }}
                >
                    <div>
                        <label className="text-xs font-medium">Location name</label>
                        <Input
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="Location name"
                            className="mt-1"
                        />
                    </div>
                    <div>
                        <label className="text-xs font-medium">Address line 1</label>
                        <Input
                            value={addressLine}
                            onChange={(e) => setAddressLine(e.target.value)}
                            placeholder="123 Main St"
                            className="mt-1"
                        />
                    </div>
                    <div>
                        <label className="text-xs font-medium">Address line 2</label>
                        <Input
                            value={addressLine2}
                            onChange={(e) => setAddressLine2(e.target.value)}
                            placeholder="Suite, unit, etc. (optional)"
                            className="mt-1"
                        />
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                        <div className="col-span-1">
                            <label className="text-xs font-medium">City</label>
                            <Input
                                value={city}
                                onChange={(e) => setCity(e.target.value)}
                                placeholder="City"
                                className="mt-1"
                            />
                        </div>
                        <div className="col-span-1">
                            <label className="text-xs font-medium">State</label>
                            <Input
                                value={region}
                                onChange={(e) => setRegion(e.target.value)}
                                placeholder="TX"
                                className="mt-1"
                            />
                        </div>
                        <div className="col-span-1">
                            <label className="text-xs font-medium">ZIP</label>
                            <Input
                                value={postalCode}
                                onChange={(e) => setPostalCode(e.target.value)}
                                placeholder="75001"
                                className="mt-1"
                            />
                        </div>
                    </div>

                    {/* ── Everything below has been editable on the SERVER
                        since migration 016 — `patchSiteBody` already accepts
                        phone, waterAccountNumber, notes and isActive. The UI
                        simply never exposed them, which is why the office had
                        five separate edit paths and this modal reached six
                        fields. One door, same engine. */}
                    <div>
                        <label className="text-xs font-medium">On-site phone</label>
                        <Input
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                            placeholder="(817) 555-0142"
                            className="mt-1"
                        />
                    </div>

                    <div>
                        <label className="text-xs font-medium">Portal account number</label>
                        <Input
                            value={accountNumber}
                            onChange={(e) => setAccountNumber(e.target.value)}
                            placeholder="1293-5984-529844"
                            className="mt-1"
                        />
                        <p className="mt-1 text-xs text-foreground/70">
                            The jurisdiction&apos;s reporting-portal number (Vepo / Swift / BSI Online). Without it,
                            nothing tested here can be filed — and the tech is paid the default multiplier.
                        </p>
                    </div>

                    <div>
                        <label className="text-xs font-medium">Location notes</label>
                        <textarea
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            rows={3}
                            placeholder="Anything the office needs to know before dispatching here."
                            className="mt-1 w-full rounded-md border border-border bg-card px-3 py-2 text-sm text-card-foreground"
                        />
                        <p className="mt-1 text-xs text-foreground/70">
                            Office-facing. For &ldquo;where is the device&rdquo;, use the device&apos;s own location
                            field instead — a tech in a parking lot reads that one.
                        </p>
                    </div>

                    {/* ── #1040: getting in ──────────────────────────── */}
                    <div>
                        <label className="text-xs font-medium">Getting in</label>
                        <textarea
                            value={accessNotes}
                            onChange={(e) => setAccessNotes(e.target.value)}
                            rows={2}
                            placeholder="Call the store manager first. Assembly is behind the dumpster enclosure."
                            className="mt-1 w-full rounded-md border border-border bg-card px-3 py-2 text-sm text-card-foreground"
                        />
                        <p className="mt-1 text-xs text-foreground/70">
                            What a tech needs to know before they arrive. Today this lives in the notes of whichever job
                            happened to capture it, so the next visit starts from nothing.
                        </p>
                    </div>

                    <div>
                        <label className="text-xs font-medium">When we can work here</label>
                        <Input
                            value={siteHours}
                            onChange={(e) => setSiteHours(e.target.value)}
                            placeholder="After 4pm, not Sundays"
                            className="mt-1"
                        />
                    </div>

                    {canSeeCodes ? (
                        <div>
                            <label className="text-xs font-medium">Gate / lockbox code</label>
                            <Input
                                value={gateCode}
                                onChange={(e) => setGateCode(e.target.value)}
                                placeholder="4821#"
                                className="mt-1 font-mono"
                            />
                            <p className="mt-1 text-xs text-foreground/70">
                                A credential to the customer&apos;s property. Visible only to people who can get on
                                site, never shown to the customer, and every change is logged.
                            </p>
                        </div>
                    ) : (
                        // Told, not hidden: a field that silently vanishes reads
                        // as "there is no code here".
                        <p className="rounded-md border border-border bg-muted/40 px-3 py-2 text-xs text-foreground/70">
                            {site.gate_code_on_file
                                ? "A gate code is on file for this location. You don't have permission to see or change it — ask an admin."
                                : "No gate code on file. You don't have permission to add one."}
                        </p>
                    )}

                    <label className="flex items-center gap-2 text-sm">
                        <input
                            type="checkbox"
                            checked={isActive}
                            onChange={(e) => setIsActive(e.target.checked)}
                            className="h-4 w-4"
                        />
                        <span>
                            Active location
                            <span className="ml-1 text-xs text-foreground/70">
                                — uncheck for a closed store; it stays on the customer for history.
                            </span>
                        </span>
                    </label>

                    <div className="flex items-center justify-end gap-2 pt-2">
                        <Button type="button" variant="outline" onClick={onClose}>
                            Cancel
                        </Button>
                        <Button type="submit" disabled={save.isPending}>
                            {save.isPending ? "Saving…" : "Save changes"}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
}

// ── Edit Device Modal (unchanged) ────────────────────────────────────

function EditSiteDeviceModal({ siteId, asset, onClose }: { siteId: string; asset: SiteAssetRow; onClose: () => void }) {
    const qc = useQueryClient();

    const [name, setName] = useState(asset.name ?? "");
    const [device, setDevice] = useState<BackflowDeviceFieldValues>({
        assemblyType: asset.assembly_type ?? "",
        sizeLabel: asset.size_label ?? "",
        manufacturer: asset.manufacturer ?? "",
        model: asset.model ?? "",
        serialNumber: asset.serial_number ?? "",
        pipeMaterial: asset.pipe_material ?? "",
        // `import` is a real stored source but not one this form can produce,
        // so it seeds as absent rather than being relabelled "human". If the
        // tech changes the material it becomes `human`; if they don't,
        // buildPatch never sends a source and the stored one is untouched.
        pipeMaterialSource:
            asset.pipe_material_source === "vision_confirmed" || asset.pipe_material_source === "human"
                ? asset.pipe_material_source
                : undefined,
        pipeMaterialConfidence: asset.pipe_material_confidence ?? undefined,
    });
    const { assemblyType, sizeLabel, manufacturer, model, serialNumber } = device;
    const patchDevice = (patch: Partial<BackflowDeviceFieldValues>) => setDevice((d) => ({ ...d, ...patch }));
    const [serviceType, setServiceType] = useState(asset.service_type ?? "");
    const [serviceTypeOther, setServiceTypeOther] = useState(asset.service_type_other ?? "");
    const [meterNumber, setMeterNumber] = useState(asset.meter_number ?? "");
    const [locationDescription, setLocationDescription] = useState(asset.location_description ?? "");

    const needsMeter = isDetectorAssembly(assemblyType);

    function buildPatch() {
        const body: Record<string, unknown> = {};
        const str = (val: string) => val.trim() || null;
        if (name.trim() !== (asset.name ?? "")) body.name = name.trim() || undefined;
        if (sizeLabel !== (asset.size_label ?? "")) body.sizeLabel = str(sizeLabel);
        if (serialNumber !== (asset.serial_number ?? "")) body.serialNumber = str(serialNumber);
        if (manufacturer !== (asset.manufacturer ?? "")) body.manufacturer = str(manufacturer);
        if (model !== (asset.model ?? "")) body.model = str(model);
        if (assemblyType !== (asset.assembly_type ?? "")) body.assemblyType = str(assemblyType);
        if (serviceType !== (asset.service_type ?? "")) body.serviceType = str(serviceType);
        if (serviceTypeOther !== (asset.service_type_other ?? "")) body.serviceTypeOther = str(serviceTypeOther);
        if (meterNumber !== (asset.meter_number ?? "")) body.meterNumber = str(meterNumber);
        if (locationDescription !== (asset.location_description ?? ""))
            body.locationDescription = str(locationDescription);
        // Material and its provenance travel together or not at all — the
        // server rejects either half alone, and so does the database.
        if ((device.pipeMaterial ?? "") !== (asset.pipe_material ?? "")) {
            if (device.pipeMaterial) {
                body.pipeMaterial = device.pipeMaterial;
                body.pipeMaterialSource = device.pipeMaterialSource ?? "human";
                if (device.pipeMaterialConfidence) body.pipeMaterialConfidence = device.pipeMaterialConfidence;
            } else {
                // Explicit null clears the material AND its provenance, putting
                // the device back to "nobody has answered".
                body.pipeMaterial = null;
            }
        }
        return body;
    }

    const save = useMutation({
        mutationFn: () => api.patch(`/api/assets/${asset.id}`, buildPatch()),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["site-summary", siteId] });
            qc.invalidateQueries({ queryKey: ["site-trade-assets", siteId] });
            toast.success("Device updated");
            onClose();
        },
        onError: (e) => toast.error(deviceErrorMessage(e, "Couldn't update device")),
    });

    const hasChanges = Object.keys(buildPatch()).length > 0;

    return (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
            <div
                className="w-full max-w-xl rounded-lg border bg-card shadow-xl max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between border-b px-4 py-3">
                    <h3 className="text-sm font-semibold">Edit device</h3>
                    <button type="button" onClick={onClose} className="text-foreground/80 hover:text-foreground">
                        <X className="h-4 w-4" />
                    </button>
                </div>

                <form
                    className="p-4 space-y-4"
                    onSubmit={(e) => {
                        e.preventDefault();
                        save.mutate();
                    }}
                >
                    <div>
                        <label className="text-xs font-medium">Name / label</label>
                        <Input value={name} onChange={(e) => setName(e.target.value)} />
                    </div>

                    <BackflowDeviceFields value={device} onChange={patchDevice} siteId={siteId} />

                    <div>
                        <label className="text-xs font-medium">Serves</label>
                        <select
                            value={serviceType}
                            onChange={(e) => setServiceType(e.target.value)}
                            className="mt-1 w-full rounded-lg border bg-background px-3 py-2 text-sm"
                        >
                            <option value="">— None —</option>
                            {SERVICE_TYPES.map((t) => (
                                <option key={t.value} value={t.value}>
                                    {t.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    {(serviceType === "domestic_other" || serviceType === "other") && (
                        <div>
                            <label className="text-xs font-medium">Describe what it serves</label>
                            <Input value={serviceTypeOther} onChange={(e) => setServiceTypeOther(e.target.value)} />
                        </div>
                    )}

                    {needsMeter && (
                        <div>
                            <label className="text-xs font-medium">Meter #</label>
                            <Input value={meterNumber} onChange={(e) => setMeterNumber(e.target.value)} />
                        </div>
                    )}

                    <div>
                        <label className="text-xs font-medium">Location on property</label>
                        <Input
                            value={locationDescription}
                            onChange={(e) => setLocationDescription(e.target.value)}
                            placeholder='e.g. "At the meter, right side of driveway"'
                        />
                        {/* #819 B10 M10: same guidance as the add-device modal —
                            one field answers "where is it", and it names its
                            reader (the Job Notes pattern). */}
                        <p className="text-xs text-foreground/80 mt-1">
                            A technician reads this to find the device in a parking lot. This field is the one answer to
                            &ldquo;where is it&rdquo; — don&apos;t repeat it in the name or the job summary.
                        </p>
                    </div>

                    <div className="flex justify-end gap-2 pt-2">
                        <Button type="button" size="sm" variant="outline" onClick={onClose}>
                            Cancel
                        </Button>
                        <Button type="submit" size="sm" disabled={save.isPending || !hasChanges}>
                            {save.isPending ? "Saving…" : "Save changes"}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
}
